/**
 * @file publish-rules.ts
 * @description Главное действие конструктора и итог публикации (phase19.16
 * Р1-6а; Р3а — SB105, SB126, SB102; uiux-r3 §3.1 «Главное действие», §3.2).
 *
 * Публикаций две и они раздельны (SB126): вид сайта (SB50–SB60) и страницы с
 * местами (черновик → версия). Правила — чистой функцией, без отрисовки:
 *  - изменений нет → кнопка неактивна, подпись «Всё опубликовано»;
 *  - изменена только открытая видимая страница, ошибок нет → «Опубликовать
 *    страницу» без окна (тост «Вернуть как было»);
 *  - изменено ещё что-то (вид сайта, другие страницы, места), страница
 *    скрыта, с ошибками или с новым адресом (Р3в: галочка переадресации,
 *    SB131) → окно публикации;
 *  - нет права публикации контента → неактивна с подписью «Правки сохранены
 *    в черновике…» (SB79 — только текст); вид сайта при этом публикуется
 *    окном, если на него хватает прав.
 */

/** Подпись неактивной кнопки без права публикации контента (SB105). */
export const PAGE_DRAFT_SAVED_TEXT = "Правки сохранены в черновике. Опубликует сотрудник с правом публикации контента";

/** Открытая страница для главного действия. */
export interface MainActionPage {
  /** Черновик отличается от версии на сайте (или версии нет). */
  hasChanges: boolean;
  isPublished: boolean;
  /** У страницы есть версия на сайте. */
  hasLive: boolean;
  /** Ошибки проверок (стоп-лист, SB127) — нужна галочка в окне. */
  hasErrors: boolean;
  /** Р3в (SB131): адрес на сайте сменится — окно с галочкой «старый будет переадресован». */
  slugChanged?: boolean;
}

export interface MainActionInput {
  /** В черновике вида сайта есть изменения. */
  viewChanged: boolean;
  /** Часть изменений вида нельзя опубликовать по правам (SB60). */
  viewBlockedByRights: boolean;
  /** Открытая страница; `null` — экран магазина, шаблон товара или страница не загружена. */
  page: MainActionPage | null;
  /** Есть неопубликованные правки других страниц и мест. */
  otherPagesChanged: boolean;
  canPublishContent: boolean;
}

export type MainAction =
  /** Окно публикации (вид сайта и/или страницы). */
  | { kind: "dialog"; label: string }
  /** Опубликовать открытую страницу сразу, без окна. */
  | { kind: "page-now"; label: string }
  | { kind: "disabled"; label: string; reason: string | null };

export function builderMainAction({ viewChanged, viewBlockedByRights, page, otherPagesChanged, canPublishContent }: MainActionInput): MainAction {
  const pagesChanged = (page?.hasChanges ?? false) || otherPagesChanged;
  if (!viewChanged && !pagesChanged) return { kind: "disabled", label: "Всё опубликовано", reason: null };
  if (!canPublishContent) {
    // Вид сайта публикуется своим правом; страницы ждут сотрудника с правом публикации.
    if (viewChanged && !viewBlockedByRights) return { kind: "dialog", label: "Опубликовать" };
    return { kind: "disabled", label: "Опубликовать", reason: pagesChanged ? PAGE_DRAFT_SAVED_TEXT : null };
  }
  if (page?.hasChanges && !viewChanged && !otherPagesChanged && page.isPublished && page.hasLive && !page.hasErrors && !page.slugChanged) {
    return { kind: "page-now", label: "Опубликовать страницу" };
  }
  // Чужие правки вида без права и нет страниц — публиковать нечего.
  if (viewBlockedByRights && !pagesChanged) return { kind: "disabled", label: "Опубликовать", reason: null };
  return { kind: "dialog", label: "Опубликовать" };
}

/** Пункт страницы в окне публикации (минимум полей `SitePageChangesDto`). */
export interface PublishItemDefaultInput {
  pageId: string;
  isPublished: boolean;
  hasChanges: boolean;
  draft: { editedBy: { id: string } | null };
}

/**
 * Отмечен ли пункт по умолчанию (SB105): открытая видимая страница с
 * правками — да; чужие страницы с правками и скрытые — нет. Свои правки на
 * других страницах — тоже нет [уточнить: SB105 называет только открытую].
 */
export function pageItemDefaultChecked(item: PublishItemDefaultInput, openPageId: string | null): boolean {
  return item.pageId === openPageId && item.isPublished && item.hasChanges;
}

type Outcome = "ok" | "failed" | "skipped";

/** Итог публикации одной фразой: сначала вид сайта, затем страницы (SB102). */
export function publishOutcomeText(outcome: { view: Outcome; pages: Outcome; titles?: readonly string[] }): string {
  const { view, pages } = outcome;
  const titles = (outcome.titles ?? []).map((t) => `«${t}»`).join(", ");
  if (view === "failed") return "Не удалось опубликовать — сайт остался прежним, черновик сохранён.";
  if (view === "ok" && pages === "failed") {
    return `Вид сайта опубликован. Страницы не опубликованы${titles ? `: ${titles}` : ""} — обновите окно и повторите.`;
  }
  if (pages === "failed") return "Страницы не опубликованы — сайт остался прежним, черновик сохранён.";
  if (view === "ok" && pages === "ok") return "Вид сайта и страницы опубликованы. Покупатели увидят их в течение минуты.";
  if (view === "ok") return "Вид сайта опубликован. Покупатели увидят его в течение минуты.";
  const count = outcome.titles?.length ?? 0;
  if (count === 1) return `Страница ${titles} опубликована. Покупатели увидят её в течение минуты.`;
  return count > 1 ? `Опубликовано страниц: ${count}. Покупатели увидят их в течение минуты.` : "Опубликовано. Покупатели увидят изменения в течение минуты.";
}

/** Короткие подписи главного действия для узкой нижней полосы телефона (360). */
const SHORT_ACTION_LABEL: Readonly<Record<string, string>> = {
  "Опубликовать страницу": "Опубликовать",
  "Всё опубликовано": "Опубликовано",
};

export function shortActionLabel(label: string): string {
  return SHORT_ACTION_LABEL[label] ?? label;
}
